import {
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
  useThread,
} from '@assistant-ui/react';
import type { FC } from 'react';
import { ArrowDownIcon, Info, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarkdownText } from './MarkdownText';
import { TooltipIconButton } from './TooltipIconButton';
import { MessageFeedbackWrapper } from './MessageFeedbackWrapper';
import { ChatbotAvatar } from './ChatbotAvatar';
import { TypingIndicator } from './TypingIndicator';
import { useChatbotConfig } from '../../hooks/useChatbotConfig';
import { useScrollToMessage } from '../../hooks/useScrollToMessage';
import { useFeedback } from '../../hooks/useFeedback';
import { CHATBOT_CONSTANTS } from '../../constants';

type ThreadProps = {
  setViewportRef: (element: HTMLDivElement | null) => void;
};

export const Thread: FC<ThreadProps> = ({ setViewportRef }) => {
  return (
    <ThreadPrimitive.Root
      className='box-border flex flex-col overflow-hidden h-full bg-background'
      style={{
        ['--thread-max-width' as string]: '42rem',
      }}
    >
      <ThreadPrimitive.Viewport
        ref={setViewportRef}
        className='flex flex-1 flex-col items-center overflow-y-auto scroll-smooth bg-inherit pt-6'
      >
        {/* Spacer to push content to bottom - mobile only, placed before content */}
        <div className='flex-grow md:hidden' />

        <ThreadWelcome />

        <ThreadPrimitive.Messages
          components={{
            UserMessage: UserMessage,
            EditComposer: EditComposer,
            AssistantMessage: AssistantMessage,
          }}
        />

        {/* Spacer to push disclaimer to bottom - desktop only */}
        <div className='max-md:!hidden md:block md:flex-grow' />

        {/* Disclaimer */}
        <div className='flex items-start justify-center gap-1 w-full max-w-[var(--thread-max-width)] px-2 pb-2'>
          <Info className='h-4 w-4 shrink-0 text-muted-foreground' />
          <div className='text-xs text-muted-foreground'>
            <span className='block'>
              AI can make mistakes. Please verify important information.
            </span>
            <span className='block'>
              Do not share personal or identifying information.
            </span>
          </div>
        </div>
      </ThreadPrimitive.Viewport>

      {/* Input container - fixed at bottom, not scrollable */}
      <div className='flex-none px-3 pt-3 pb-0 flex w-full flex-col items-center justify-end bg-background shadow-[0_-3.307px_13.227px_0_rgba(0,0,0,0.08)] relative'>
        <ThreadScrollToBottom />
        <div className='w-full max-w-[var(--thread-max-width)]'>
          <Composer />
        </div>
      </div>
    </ThreadPrimitive.Root>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip='Scroll to bottom'
        variant='outline'
        className='absolute -top-8 rounded-full disabled:invisible'
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  const { config } = useChatbotConfig();
  const welcomeText = config?.welcomeText;

  return (
    <div className='flex w-full max-w-[var(--thread-max-width)] pb-4'>
      <div className='flex px-3 w-full flex gap-2 items-start'>
        <div className='flex-shrink-0'>
          <ChatbotAvatar />
        </div>
        <div className='flex items-center min-h-10'>
          <p className='text-foreground text-lg leading-7 whitespace-pre-line'>
            {welcomeText}
          </p>
        </div>
      </div>
    </div>
  );
};

const Composer: FC = () => {
  const placeholder = 'Type here...';

  return (
    <ComposerPrimitive.Root className='flex w-full flex-col rounded-3xl border bg-muted/50 px-3 pb-2 pt-3 shadow-none outline-none transition-colors ease-in'>
      <ComposerPrimitive.Input
        name='input'
        rows={1}
        autoFocus
        placeholder={placeholder}
        maxLength={CHATBOT_CONSTANTS.MAX_INPUT_LENGTH}
        className='placeholder:text-muted-foreground max-h-40 min-h-[36px] w-full resize-none border-none bg-transparent px-2 py-1 text-sm outline-none focus:ring-0 disabled:cursor-not-allowed [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-full'
      />
      <div className='flex w-full items-center justify-end'>
        <ComposerAction />
      </div>
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC = () => {
  const { color, colorOverTheme } = useChatbotConfig();

  return (
    <>
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip='Send'
            variant='default'
            className='h-10 w-10 p-2 transition-opacity ease-in rounded-full'
            style={{ backgroundColor: color }}
          >
            <Send className='size-5' style={{ color: colorOverTheme }} />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            tooltip='Cancel'
            variant='default'
            className='h-10 w-10 p-2 transition-opacity ease-in rounded-full'
            style={{ backgroundColor: color }}
          >
            <CircleStopIcon style={{ color: colorOverTheme }} />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root className='grid auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] gap-y-2 [&:where(>*)]:col-start-2 w-full max-w-[var(--thread-max-width)] py-4 px-3'>
      {/* Message bubble with "You" label inside */}
      <div className='bg-muted text-foreground [overflow-wrap:anywhere] rounded-lg p-3 col-start-2 row-start-2 max-w-[calc(var(--thread-max-width)*0.8)] text-lg flex flex-col items-end'>
        <MessagePrimitive.Content />
        {/* "You" label */}
        <span className='text-muted-foreground text-sm font-normal mt-1'>
          You
        </span>
      </div>
    </MessagePrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <ComposerPrimitive.Root className='bg-muted my-4 flex w-full max-w-[var(--thread-max-width)] flex-col gap-2 rounded-xl'>
      <ComposerPrimitive.Input className='text-foreground flex h-8 w-full resize-none bg-transparent p-4 pb-0 outline-none' />

      <div className='mx-2 mb-2 flex items-center justify-center gap-2 self-end'>
        <ComposerPrimitive.Cancel asChild>
          <Button variant='ghost'>Cancel</Button>
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send asChild>
          <Button>Send</Button>
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
};

const AssistantMessage: FC = () => {
  const thread = useThread();
  const message = useMessage();

  // Check if thread is running and this is the last message
  const isRunning = thread.isRunning;
  const isLastMessage = message.isLast && message.role === 'assistant';
  const shouldShowTyping = isRunning && isLastMessage;

  // Auto-scroll to top of message when it finishes typing
  const messageRef = useScrollToMessage(
    isLastMessage,
    shouldShowTyping,
    isRunning,
  );

  return (
    <MessagePrimitive.Root
      ref={messageRef}
      className='w-full max-w-[var(--thread-max-width)] py-4 px-3'
    >
      {/* Row 1: Avatar + Content (top-aligned, but content wrapper centers single-line text) */}
      <div className='flex gap-2 items-start'>
        <div className='flex-shrink-0'>
          <ChatbotAvatar />
        </div>

        {/* Content column - takes remaining space after avatar */}
        <div className='flex-1'>
          {/* Content wrapper - centers single-line text with avatar, expands for multi-line */}
          <div className='flex items-center min-h-10'>
            <div
              className={`text-foreground max-w-[calc(var(--thread-max-width)*0.8)] [overflow-wrap:anywhere] leading-7 text-lg ${shouldShowTyping ? 'mt-4' : ''}`}
            >
              {shouldShowTyping ? (
                <TypingIndicator />
              ) : (
                <>
                  <MessagePrimitive.Content
                    components={{ Text: MarkdownText }}
                  />
                  <MessageError />
                </>
              )}
            </div>
          </div>

          {/* Feedback buttons aligned with text content */}
          {!shouldShowTyping && (
            <div className='mt-3'>
              <MessageFeedbackWrapper />
            </div>
          )}
        </div>
      </div>

      {/* Thank you message - shown after feedback is submitted as separate row */}
      {!shouldShowTyping && <FeedbackThankYou />}
    </MessagePrimitive.Root>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className='border-destructive bg-destructive/10 text-destructive mt-2 rounded-md border p-2 text-sm'>
        <ErrorPrimitive.Message className='line-clamp-2' />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const FeedbackThankYou: FC = () => {
  const message = useMessage();
  const { isSubmitted, messageId: feedbackMessageId } = useFeedback();

  // Check feedback completion from both Redux (for immediate display) and metadata (for persistence)
  const feedbackCompleted = message?.metadata?.custom?.feedbackState?.completed;
  const isThisMessageSubmitted =
    isSubmitted && feedbackMessageId === message.id;

  // Show thank you if either Redux shows just submitted OR metadata shows completed
  if (!isThisMessageSubmitted && !feedbackCompleted) {
    return null;
  }

  return (
    <div className='mt-3 flex gap-2 items-start'>
      <div className='flex-shrink-0'>
        <ChatbotAvatar />
      </div>

      <div className='flex-1'>
        <div className='flex items-center min-h-10'>
          <div className='text-foreground leading-7 text-lg'>
            Thank you for your feedback!
          </div>
        </div>
      </div>
    </div>
  );
};

const CircleStopIcon = ({ style }: { style?: React.CSSProperties }) => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 16 16'
      fill='currentColor'
      width='16'
      height='16'
      style={style}
    >
      <rect width='10' height='10' x='3' y='3' rx='2' />
    </svg>
  );
};
